import { FC, useMemo, useState } from 'react';
import { Project } from '@brg/api';
import { MEDIA } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LayoutConst } from 'core';
import { WorldIcon } from '../../../../../_ftbl/main/assets/icons/WorldIcon';
import { formatDate } from '../../../../../_ftbl/main/helpers/dateFormatting';
import { parseFtblMatchInfo } from './parseFtblMatchInfo';
import { FtblMatchInfo } from './ftblMatchInfo.types';
import { hasFtblMatchScore } from './hasFtblMatchScore';
import { getFlagUrl } from "../../../../../_ftbl/main/helpers/getFlagUrl";

const MatchFTBLRoot = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100%;
  background-color: #E6E9FF;
  display: flex;
  flex-direction: column;
`;

const HomeFlagSection = styled.div<{ $isMatchNotStarted?: boolean }>`
  padding: 24px 24px ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '9px' : '8px')} 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.l}px) {
      padding: 16px 16px ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '5px' : '8px')} 16px;
  }

  @media ${MEDIA.MOBILE} {
      padding: 8px 8px ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '1px' : '4px')} 8px;
  }
`;

const GuestFlagSection = styled.div<{ $isMatchNotStarted?: boolean }>`
  padding: ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '9px' : '8px')} 24px 24px 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.l}px) {
      padding: ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '5px' : '8px')} 16px 16px 16px;
  }

  @media ${MEDIA.MOBILE} {
      padding: ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '1px' : '4px')} 8px 8px 8px;
  }
`;

const MatchFlagImage = styled.img`
  display: block;
  width: 100%;
  max-height: 309px;
  height: auto;
  object-fit: cover;
`;

const MatchFlagFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-height: 309px;
  aspect-ratio: 5 / 3;
  box-sizing: border-box;
  background-color: #F5F5F5;
`;

const MatchFlagFallbackTeamName = styled.div`
  margin-top: 16px;
  color: var(--stroke, #BBB);
  text-align: center;
  font-family: "Alumni Sans", sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-transform: uppercase;
`;

const TeamsNamesBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
      padding: 0 24px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
      padding: 0 16px;
  }

  @media ${MEDIA.MOBILE} {
      padding: 0 8px;
  }
`;

const TeamRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TeamsNoScoreLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
`;

const TeamsNamesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
  align-self: stretch;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
      gap: 29px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
      gap: 15px;
  }

  @media ${MEDIA.MOBILE} {
      gap: 19px;
  }
`;

const TeamsNamesColumnNumbers = styled(TeamsNamesColumn)`
    gap: 12px;

    @media (max-width: ${LayoutConst.screen.mac13}px) {
        gap: 1px;
    }

    @media (max-width: ${LayoutConst.screen.l}px) {
        gap: 1px;
    }

    @media ${MEDIA.MOBILE} {
        gap: 5px;
    }
`

const TeamName = styled.div`
  color: var(--Gallllery-Black73, #545453);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 27px;
  letter-spacing: -0.46px;
  margin: 7px 0;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 23px;
    margin: 3px 0;
  }
    

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 20px;
    line-height: 25px;
    letter-spacing: -0.4px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.3px;
  }
`;

const TeamScorePenalty = styled.span`
  color: #909090;
`;

const TeamScore = styled.div`
  color: var(--Gallllery-Black73, #545453);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-style: normal;
  font-size: 43px;
  font-weight: 700;
  line-height: 51px; /* 118.605% */
  letter-spacing: -0.86px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 34px;
    font-weight: 600;
    font-style: normal;
    line-height: 41px;
    letter-spacing: -0.68px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 26px;
    line-height: 32px;
    letter-spacing: -0.52px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 23px;
    line-height: 27px;
    letter-spacing: -0.46px;
  }
`;

const MatchDateTime = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  color: var(--Gallllery-Black, #151514);
  text-align: right;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-size: 23px;
  font-style: normal;
  font-weight: 400;
  line-height: 27px;
  letter-spacing: -0.46px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
      font-size: 17px;
      line-height: 22px;
      letter-spacing: -0.34px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.3px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`;

type MatchFlagProps = {
    countryId: number;
    teamName: string;
};

const MatchFlag: FC<MatchFlagProps> = ({ countryId, teamName }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
        return (
            <MatchFlagFallback>
                <WorldIcon />
                <MatchFlagFallbackTeamName>{teamName}</MatchFlagFallbackTeamName>
            </MatchFlagFallback>
        );
    }

    return (
        <MatchFlagImage
            src={getFlagUrl(countryId)}
            alt={teamName}
            onError={() => setHasError(true)}
        />
    );
};

type MatchFTBLProps = {
    project: Project;
};

export const MatchFTBL: FC<MatchFTBLProps> = ({ project }) => {
    const { i18n } = useTranslation();

    const matchInfo = useMemo<FtblMatchInfo | null>(
        () => parseFtblMatchInfo(project?.additionalInfo?.MatchInfo),
        [project],
    );

    const hasScore = matchInfo ? hasFtblMatchScore(matchInfo) : false;
    const isMatchNotStarted = !hasScore;
    const hasPenaltyScore = matchInfo?.HomeTeamScorePenalty != null && matchInfo?.GuestTeamScorePenalty != null;

    const matchDate = useMemo(
        () => (matchInfo ? formatDate(matchInfo.Date, i18n.language, 'day') : ''),
        [matchInfo, i18n.language],
    );

    const matchTime = useMemo(
        () => (matchInfo ? formatDate(matchInfo.Date, i18n.language, 'time') : ''),
        [matchInfo, i18n.language],
    );

    if (!matchInfo) {
        return <MatchFTBLRoot />;
    }

    return (
        <MatchFTBLRoot>
            <HomeFlagSection $isMatchNotStarted={isMatchNotStarted}>
                <MatchFlag
                    countryId={matchInfo.HomeTeamCountryId}
                    teamName={matchInfo.HomeTeamName}
                />
            </HomeFlagSection>

            <TeamsNamesBlock>
                {hasScore ? (
                    <>
                      <TeamsNamesColumnNumbers>
                        <TeamRow>
                          <TeamName>{matchInfo.HomeTeamName}</TeamName>
                          <TeamScore>
                            {matchInfo.HomeTeamScore}
                            {hasPenaltyScore && <> {' '}<TeamScorePenalty>({matchInfo.HomeTeamScorePenalty})</TeamScorePenalty></>}
                          </TeamScore>
                        </TeamRow>
                        <TeamRow>
                          <TeamName>{matchInfo.GuestTeamName}</TeamName>
                          <TeamScore>
                            {matchInfo.GuestTeamScore}
                            {hasPenaltyScore && <> {' '}<TeamScorePenalty>({matchInfo.GuestTeamScorePenalty})</TeamScorePenalty></>}
                          </TeamScore>
                        </TeamRow>
                      </TeamsNamesColumnNumbers>
                    </>
                ) : (
                    <TeamsNoScoreLayout>
                        <TeamsNamesColumn>
                            <TeamName>{matchInfo.HomeTeamName}</TeamName>
                            <TeamName>{matchInfo.GuestTeamName}</TeamName>
                        </TeamsNamesColumn>
                        <MatchDateTime>
                            <div>{matchDate}</div>
                            <div>{matchTime}</div>
                        </MatchDateTime>
                    </TeamsNoScoreLayout>
                )}
            </TeamsNamesBlock>

            <GuestFlagSection $isMatchNotStarted={isMatchNotStarted}>
                <MatchFlag
                    countryId={matchInfo.GuestTeamCountryId}
                    teamName={matchInfo.GuestTeamName}
                />
            </GuestFlagSection>
        </MatchFTBLRoot>
    );
};
import { FC, useMemo, useState } from 'react';
import { Project } from '@brg/api';
import { MEDIA } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LayoutConst } from 'core';
import { WorldIcon } from '../../../../../_ftbl/main/assets/icons/WorldIcon';
import { formatDate } from '../../../../../_ftbl/main/helpers/dateFormatting';
import { parseFtblMatchInfo } from './parseFtblMatchInfo';
import { FtblMatchInfo } from './ftblMatchInfo.types';
import { hasFtblMatchScore } from './hasFtblMatchScore';
import { getFlagUrl } from "../../../../../_ftbl/main/helpers/getFlagUrl";

const MatchFTBLRoot = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100%;
  background-color: #E6E9FF;
  display: flex;
  flex-direction: column;
`;

const HomeFlagSection = styled.div<{ $isMatchNotStarted?: boolean }>`
  padding: 24px 24px ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '9px' : '8px')} 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.l}px) {
      padding: 16px 16px ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '5px' : '8px')} 16px;
  }

  @media ${MEDIA.MOBILE} {
      padding: 8px 8px ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '1px' : '4px')} 8px;
  }
`;

const GuestFlagSection = styled.div<{ $isMatchNotStarted?: boolean }>`
  padding: ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '9px' : '8px')} 24px 24px 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.l}px) {
      padding: ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '5px' : '8px')} 16px 16px 16px;
  }

  @media ${MEDIA.MOBILE} {
      padding: ${({ $isMatchNotStarted }) => ($isMatchNotStarted ? '1px' : '4px')} 8px 8px 8px;
  }
`;

const MatchFlagImage = styled.img`
  display: block;
  width: 100%;
  max-height: 309px;
  height: auto;
  object-fit: cover;
`;

const MatchFlagFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-height: 309px;
  aspect-ratio: 5 / 3;
  box-sizing: border-box;
  background-color: #F5F5F5;
`;

const MatchFlagFallbackTeamName = styled.div`
  margin-top: 16px;
  color: var(--stroke, #BBB);
  text-align: center;
  font-family: "Alumni Sans", sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-transform: uppercase;
`;

const TeamsNamesBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
      padding: 0 24px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
      padding: 0 16px;
  }

  @media ${MEDIA.MOBILE} {
      padding: 0 8px;
  }
`;

const TeamRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TeamsNoScoreLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
`;

const TeamsNamesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
  align-self: stretch;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
      gap: 29px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
      gap: 15px;
  }

  @media ${MEDIA.MOBILE} {
      gap: 19px;
  }
`;

const TeamsNamesColumnNumbers = styled(TeamsNamesColumn)`
    gap: 12px;

    @media (max-width: ${LayoutConst.screen.mac13}px) {
        gap: 1px;
    }

    @media (max-width: ${LayoutConst.screen.l}px) {
        gap: 1px;
    }

    @media ${MEDIA.MOBILE} {
        gap: 5px;
    }
`

const TeamName = styled.div`
  color: var(--Gallllery-Black73, #545453);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 27px;
  letter-spacing: -0.46px;
  margin: 7px 0;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 23px;
    margin: 3px 0;
  }
    

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 20px;
    line-height: 25px;
    letter-spacing: -0.4px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.3px;
  }
`;

const TeamScorePenalty = styled.span`
  color: #909090;
`;

const TeamScore = styled.div`
  color: var(--Gallllery-Black73, #545453);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-style: normal;
  font-size: 43px;
  font-weight: 700;
  line-height: 51px; /* 118.605% */
  letter-spacing: -0.86px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 34px;
    font-weight: 600;
    font-style: normal;
    line-height: 41px;
    letter-spacing: -0.68px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 26px;
    line-height: 32px;
    letter-spacing: -0.52px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 23px;
    line-height: 27px;
    letter-spacing: -0.46px;
  }
`;

const MatchDateTime = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  color: var(--Gallllery-Black, #151514);
  text-align: right;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-size: 23px;
  font-style: normal;
  font-weight: 400;
  line-height: 27px;
  letter-spacing: -0.46px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
      font-size: 17px;
      line-height: 22px;
      letter-spacing: -0.34px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.3px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`;

type MatchFlagProps = {
    countryId: number;
    teamName: string;
};

const MatchFlag: FC<MatchFlagProps> = ({ countryId, teamName }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
        return (
            <MatchFlagFallback>
                <WorldIcon />
                <MatchFlagFallbackTeamName>{teamName}</MatchFlagFallbackTeamName>
            </MatchFlagFallback>
        );
    }

    return (
        <MatchFlagImage
            src={getFlagUrl(countryId)}
            alt={teamName}
            onError={() => setHasError(true)}
        />
    );
};

type MatchFTBLProps = {
    project: Project;
};

export const MatchFTBL: FC<MatchFTBLProps> = ({ project }) => {
    const { i18n } = useTranslation();

    const matchInfo = useMemo<FtblMatchInfo | null>(
        () => parseFtblMatchInfo(project?.additionalInfo?.MatchInfo),
        [project],
    );

    const hasScore = matchInfo ? hasFtblMatchScore(matchInfo) : false;
    const isMatchNotStarted = !hasScore;
    const hasPenaltyScore = matchInfo?.HomeTeamScorePenalty != null && matchInfo?.GuestTeamScorePenalty != null;

    const matchDate = useMemo(
        () => (matchInfo ? formatDate(matchInfo.Date, i18n.language, 'day') : ''),
        [matchInfo, i18n.language],
    );

    const matchTime = useMemo(
        () => (matchInfo ? formatDate(matchInfo.Date, i18n.language, 'time') : ''),
        [matchInfo, i18n.language],
    );

    if (!matchInfo) {
        return <MatchFTBLRoot />;
    }

    return (
        <MatchFTBLRoot>
            <HomeFlagSection $isMatchNotStarted={isMatchNotStarted}>
                <MatchFlag
                    countryId={matchInfo.HomeTeamCountryId}
                    teamName={matchInfo.HomeTeamName}
                />
            </HomeFlagSection>

            <TeamsNamesBlock>
                {hasScore ? (
                    <>
                      <TeamsNamesColumnNumbers>
                        <TeamRow>
                          <TeamName>{matchInfo.HomeTeamName}</TeamName>
                          <TeamScore>
                            {matchInfo.HomeTeamScore}
                            {hasPenaltyScore && <> {' '}<TeamScorePenalty>({matchInfo.HomeTeamScorePenalty})</TeamScorePenalty></>}
                          </TeamScore>
                        </TeamRow>
                        <TeamRow>
                          <TeamName>{matchInfo.GuestTeamName}</TeamName>
                          <TeamScore>
                            {matchInfo.GuestTeamScore}
                            {hasPenaltyScore && <> {' '}<TeamScorePenalty>({matchInfo.GuestTeamScorePenalty})</TeamScorePenalty></>}
                          </TeamScore>
                        </TeamRow>
                      </TeamsNamesColumnNumbers>
                    </>
                ) : (
                    <TeamsNoScoreLayout>
                        <TeamsNamesColumn>
                            <TeamName>{matchInfo.HomeTeamName}</TeamName>
                            <TeamName>{matchInfo.GuestTeamName}</TeamName>
                        </TeamsNamesColumn>
                        <MatchDateTime>
                            <div>{matchDate}</div>
                            <div>{matchTime}</div>
                        </MatchDateTime>
                    </TeamsNoScoreLayout>
                )}
            </TeamsNamesBlock>

            <GuestFlagSection $isMatchNotStarted={isMatchNotStarted}>
                <MatchFlag
                    countryId={matchInfo.GuestTeamCountryId}
                    teamName={matchInfo.GuestTeamName}
                />
            </GuestFlagSection>
        </MatchFTBLRoot>
    );
};
